import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Loader2, Briefcase, Calculator, KeyRound } from "lucide-react";
import api from "@/lib/api";
import { CHARGES, STATE_GUIDES } from "@/data/guides";
import { Seo, SITE_URL, breadcrumbs } from "@/components/Seo";
import { List, Section, PenaltyTable, Crumbs, ReviewNotice } from "@/components/GuideParts";
import { Button } from "@/components/ui/button";

const StateDetails = ({ s, place }) => (
  <>
    <p data-testid="charge-state-summary" className="mt-5 text-ink-700 leading-relaxed">{s.summary}</p>
    {s.statute && <p className="mt-3 font-mono text-xs text-terracotta">{s.statute}</p>}
    <Section title={`Penalties in ${place}`}><PenaltyTable rows={s.classifications} a="level" b="penalty" testid="charge-state-penalties" /></Section>
    <Section title={`Key ${place} rules`}><List items={s.key_facts} testid="charge-state-facts" /></Section>
    {s.diversion && <Section title="Diversion, sealing & second chances"><p className="mt-4 text-ink-600 leading-relaxed">{s.diversion}</p></Section>}
  </>
);

const NationalDetails = ({ n, compact }) => (
  <>
    {!compact && n.overview.map((p) => <p key={p.slice(0, 30)} className="mt-5 text-ink-700 leading-relaxed">{p}</p>)}
    <Section title="What the prosecution must prove"><List items={n.elements} testid="charge-elements" /></Section>
    {!compact && <Section title="Typical penalties across the U.S."><PenaltyTable rows={n.penalty_ranges} a="level" b="typical" testid="charge-national-penalties" /></Section>}
    <Section title="Common defenses"><List items={n.common_defenses} testid="charge-defenses" /></Section>
    <Section title="Consequences beyond the sentence"><List items={n.collateral} testid="charge-collateral" /></Section>
    <Section title="What to do right now"><List items={n.first_steps} testid="charge-first-steps" /></Section>
    <Section title="Questions to ask a defense lawyer"><List items={n.questions} testid="charge-questions" /></Section>
  </>
);

const Cta = ({ stateName }) => (
  <section className="mt-14 rounded-3xl bg-forest-900 text-parchment-50 p-8">
    <h2 className="font-serif text-3xl font-semibold">Get help with your case</h2>
    <div className="mt-6 grid sm:grid-cols-3 gap-3">
      <Link to="/eligibility" data-testid="charge-cta-eligibility"><Button variant="outline" className="w-full rounded-xl h-11 gap-2 text-forest-900"><Calculator className="h-4 w-4" />Free lawyer?</Button></Link>
      <Link to="/bail" data-testid="charge-cta-bail"><Button variant="outline" className="w-full rounded-xl h-11 gap-2 text-forest-900"><KeyRound className="h-4 w-4" />Estimate bail</Button></Link>
      <Link to={stateName ? `/attorneys?state=${encodeURIComponent(stateName)}` : "/attorneys"} data-testid="charge-cta-lawyer">
        <Button className="w-full rounded-xl h-11 gap-2 bg-terracotta hover:bg-terracotta-hover text-white"><Briefcase className="h-4 w-4" />Find a lawyer</Button>
      </Link>
    </div>
  </section>
);

const StateChips = ({ charge, current }) => (
  <section className="mt-12 pt-8 border-t border-parchment-200">
    <h2 className="font-serif text-xl font-semibold text-forest-900">{charge.noun} by state</h2>
    <div data-testid="charge-state-links" className="mt-4 flex flex-wrap gap-2">
      {STATE_GUIDES.filter((s) => s.slug !== current).map((s) => (
        <Link key={s.slug} to={`/charges/${charge.slug}/${s.slug}`} className="text-xs px-3 py-1.5 rounded-full border border-parchment-200 bg-white text-ink-600 hover:border-forest-800 hover:text-forest-900 transition-colors">{s.name}</Link>
      ))}
    </div>
  </section>
);

export default function ChargeGuide() {
  const { charge: cSlug, state: sSlug } = useParams();
  const charge = CHARGES.find((c) => c.slug === cSlug);
  const st = sSlug ? STATE_GUIDES.find((s) => s.slug === sSlug) : null;
  const [loaded, setLoaded] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!charge || (sSlug && !st)) return;
    const key = `${cSlug}/${sSlug || ""}`;
    let active = true;
    setFailed(false);
    api.get(`/guides/charges/${cSlug}${st ? `/${st.slug}` : ""}`)
      .then((r) => active && setLoaded({ key, data: st ? r.data : { national: r.data } }))
      .catch(() => active && setFailed(true));
    return () => { active = false; };
  }, [cSlug, sSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!charge) return <Navigate to="/charges" replace />;
  if (sSlug && !st) return <Navigate to={`/charges/${cSlug}`} replace />;

  // only use data fetched for the current route (avoids stale national data on a state route)
  const data = loaded?.key === `${cSlug}/${sSlug || ""}` ? loaded.data : null;
  const place = st?.name;
  const path = `/charges/${cSlug}${st ? `/${st.slug}` : ""}`;
  const title = st ? `${charge.noun} in ${place}: Penalties, Defenses & What to Do` : `${charge.noun}: Penalties, Defenses & What to Do Next`;
  const description = st
    ? `${charge.noun} in ${place}: offense levels, jail and fine ranges, key state rules, diversion options and common defenses. Free plain-English guide.`
    : `${charge.noun} explained: what prosecutors must prove, typical U.S. penalties, common defenses, collateral consequences and first steps after being charged.`;
  const crumbs = [{ name: "Home", path: "/" }, { name: "Charge guides", path: "/charges" }, { name: charge.name, path: st ? `/charges/${cSlug}` : null }, ...(st ? [{ name: place }] : [])];
  const jsonLd = { "@context": "https://schema.org", "@graph": [
    { "@type": "Article", headline: title, description, url: `${SITE_URL}${path}`, dateModified: "2026-06-01", inLanguage: "en-US",
      publisher: { "@type": "Organization", name: "SentinelGuide", logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.svg` } } },
    breadcrumbs(crumbs.map((c) => ({ name: c.name, path: c.path || path }))),
  ]};

  return (
    <article data-testid="charge-guide-page" className="grain-bg min-h-screen">
      <Seo title={title} description={description} path={path} jsonLd={jsonLd} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <Crumbs items={crumbs} />
        <h1 data-testid="charge-guide-title" className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-forest-900 leading-[1.08]">
          {st ? `${charge.noun} in ${place}` : `${charge.noun} explained`}
        </h1>
        <ReviewNotice place={place} />
        {failed && <p data-testid="charge-guide-missing" className="mt-8 text-ink-600">This guide is being prepared. Please check back soon.</p>}
        {!data && !failed && <Loader2 data-testid="charge-guide-loading" className="mt-10 h-6 w-6 animate-spin text-forest-800" />}
        {data && (
          <>
            {st && <StateDetails s={data.state} place={place} />}
            <NationalDetails n={data.national} compact={!!st} />
          </>
        )}
        {st && <Link to={`/guides/${st.slug}`} data-testid="charge-state-guide-link" className="mt-10 inline-block text-sm font-medium text-terracotta hover:underline">How criminal cases work in {place} →</Link>}
        <Cta stateName={place} />
        <StateChips charge={charge} current={sSlug} />
      </div>
    </article>
  );
}
